import { httpClient } from "@utils";

import { CashoutType } from "@gp/hub";

import { BetSlipCashoutHistory } from "@api-types/agency/autocashout/BetSlipCashoutHistory";
import { BetSlipResult } from "@api-types/agency/bet-slip/BetSlipResult";
import { GenericResponse, PageableCollection } from "@api-types/common";
import { BetSlipExtended, BetSlipForShare } from "@api-types/agency/bet-slip";
import { TQuickBetFormRequest } from "@api-types/quick-play";
import { BetSlipCombinations } from "@api-types/bet-slip/BetSlipCombinations";
import { BetSlipPayoutResult } from "@api-types/bet-slip/BetSlipPayoutResult";
import { Filter } from "@api-types/my-bets/Filter";
import { IBetSlipRequest, IBetSlipResponse } from "@gp/slip";
import { BetSlip } from "@gp/models";
import { getApiRequestContext } from "@utils/api-config";
import { getApplicationRequestHeaders } from "@utils/api-config/getApplicationRequestHeader";
import { ApiRequestContextData, ContextOptions } from "@utils/api-config/types";
import { RequestContextType } from "@utils/api-config/RequestContext";
import { findValidSubmitWallet } from "@utils/bet-slip/findValidSubmitWallet";
import {
	PreparedBetSlip,
	PrepareResponse,
} from "@api-types/bet-slip/SavedSlip";
import { Freebet, FreebetAction } from "@api-types/bet-slip/Freebet";

class BetSlipApiService {
	static baseUrl(config: ApiRequestContextData | null) {
		return `${WEBPACK_BASE_ADDRESS}${config?.agencyKey}/bet-slips`;
	}

	static getRequestHeaders(config: ApiRequestContextData | null) {
		const appHeaders = getApplicationRequestHeaders(config);
		appHeaders["Content-Type" as keyof HeadersInit] = "application/json";
		return appHeaders;
	}

	static async findBetsOnAdditionalOffer(
		options: ContextOptions,
		filter: {
			eventId: string;
			page: number;
			pageSize: number;
		}
	): Promise<PageableCollection<BetSlipExtended>> {
		let query = new URLSearchParams();
		const context = getApiRequestContext(options);

		query.set(
			"embed",
			"betStatus,betSlipStatus,betSlipType,betSlipOffers,betSlipSecureWinSetting"
		);
		query.set("rpp", String(filter.pageSize));
		query.set("page", String(filter.page));
		query.set("eventIds", filter.eventId);
		query.set("sort", "dateCreated|desc");
		query.set("translate", String(true));

		return await httpClient.get(
			`${this.baseUrl(context)}/get-bet-slips-for-events?${query}`,
			this.getRequestHeaders(context)
		);
	}

	static async findBets(
		filter: Filter | null = null,
		page: number | null = 1,
		rpp: number = 10,
		options: ContextOptions,
		sort: string = "dateCreated|desc"
	): Promise<PageableCollection<BetSlipExtended>> {
		const context = getApiRequestContext(options);

		let query;
		if (filter) {
			//@ts-expect-error
			query = new URLSearchParams(filter);
		} else {
			query = new URLSearchParams();
		}
		query.set("page", String(page));
		query.set("rpp", String(rpp));
		query.set("sort", String(sort));
		query.set("translate", String(true));

		return await httpClient.get(
			`${this.baseUrl(context)}?${query}`,
			this.getRequestHeaders(context)
		);
	}

	static async getAvailableFreebets(): Promise<Freebet[]> {
		const context = getApiRequestContext();

		let bettingAccountTypeId = findValidSubmitWallet(
			App.state.rootStore.userAuthStore
		)?.accountTypeId;

		if (!bettingAccountTypeId) {
			console.warn(
				"getAvailableFreebets: no valid wallet found, aborting request"
			);
			return [];
		}

		const response = await httpClient.get(
			`${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/freebets/user?activeOnly=true&bettingAccountTypeId=${bettingAccountTypeId}`,
			this.getRequestHeaders(context)
		);

		return response.item;
	}

	static async freebetConfirmation(
		freebetId: string,
		actionType: FreebetAction
	): Promise<void> {
		const context = getApiRequestContext();

		await httpClient.put(
			`${WEBPACK_BASE_ADDRESS}${context?.agencyKey}/freebets/${freebetId}/${actionType}`,
			null,
			this.getRequestHeaders(context)
		);
	}

	static async getBet(
		slipId: string,
		embed = "",
		options?: ContextOptions
	): Promise<BetSlipExtended> {
		const context = getApiRequestContext(options);
		return await httpClient.get(
			`${this.baseUrl(context)}/${slipId}?embed=${embed}&translate=true`,
			this.getRequestHeaders(context)
		);
	}

	static getSystemCombinations(
		id: string,
		options?: ContextOptions
	): Promise<BetSlipCombinations[]> {
		const context = getApiRequestContext(options);

		return httpClient.get(
			`${this.baseUrl(context)}/${id}/combinations`,
			this.getRequestHeaders(context)
		);
	}

	static getCashoutHistory(
		id: string,
		options?: ContextOptions
	): Promise<BetSlipCashoutHistory> {
		const context = getApiRequestContext(options);
		return httpClient.get(
			`${this.baseUrl(
				context
			)}/${id}/bet-slip-jacktime-cashout-history-value`,
			this.getRequestHeaders(context)
		);
	}
	static putCancelBet(
		id: string,
		options?: ContextOptions
	): Promise<BetSlipResult> {
		const context = getApiRequestContext(options);

		return httpClient.put(
			`${this.baseUrl(context)}/${id}/cancel`,
			null,
			this.getRequestHeaders(context)
		);
	}

	static getSharedBetSlip(betSlipNumber: string): Promise<BetSlipForShare> {
		const context = getApiRequestContext();

		return httpClient.get(
			`${this.baseUrl(context)}/share/${betSlipNumber}`,
			this.getRequestHeaders(context)
		);
	}

	static async cashout(
		id: string,
		calculatedCashoutAmount: number,
		cType: CashoutType,
		isChangeAccepted: boolean,
		options?: ContextOptions
	): Promise<BetSlipPayoutResult> {
		const context = getApiRequestContext(options);

		const response = await httpClient.put(
			`${this.baseUrl(context)}/${id}/cashout`,
			JSON.stringify({
				betSlipId: id,
				oldCashoutAmount: calculatedCashoutAmount,
				cType: cType,
				isChangedAmountAccepted: isChangeAccepted,
			}),
			this.getRequestHeaders(context)
		);

		return response;
	}

	static async cashoutWithQrCode(
		id: string,
		calculatedCashoutAmount: number,
		isChangeAccepted: boolean,
		code: string,
		betSlipNumber: string,
		options?: ContextOptions
	): Promise<BetSlipPayoutResult> {
		const context = getApiRequestContext(options);

		const response = await httpClient.put(
			`${this.baseUrl(context)}/cashout-with-qrcode/${betSlipNumber}`,
			JSON.stringify({
				betSlipId: id,
				oldCashoutAmount: calculatedCashoutAmount,
				isChangedAmountAccepted: isChangeAccepted,
				code: code,
			}),
			this.getRequestHeaders(context)
		);

		return response;
	}

	static async fetchQuickBet(
		data: TQuickBetFormRequest
	): Promise<GenericResponse<BetSlipResult>> {
		const context = getApiRequestContext();

		return await httpClient.post(
			`${this.baseUrl(context)}/quick-bet-new`,
			JSON.stringify(data),
			this.getRequestHeaders(context)
		);
	}

	static async validate(
		slip: IBetSlipRequest,
		abortController: AbortController
	): Promise<IBetSlipResponse> {
		let walletId =
			findValidSubmitWallet(App.state.rootStore.userAuthStore)?.id ||
			App.state.rootStore.userAuthStore.activeWalletId;

		const options = getApiRequestContext({
			context: RequestContextType.Wallet,
			walletId: walletId,
		});
		const headers = this.getRequestHeaders(options);
		return await httpClient.post(
			`${this.baseUrl(options)}/betting-slip-validate/2`,
			JSON.stringify(slip),
			headers,
			{ signal: abortController.signal }
		);
	}

	static submit(slip: IBetSlipRequest): Promise<IBetSlipResponse> {
		let walletId =
			findValidSubmitWallet(App.state.rootStore.userAuthStore)?.id ||
			App.state.rootStore.userAuthStore.activeWalletId;

		const context = getApiRequestContext({
			context: RequestContextType.Wallet,
			walletId: walletId,
		});

		const headers = this.getRequestHeaders(context);

		return httpClient.post(
			`${this.baseUrl(context)}/betting-slip-validate/4`,
			JSON.stringify(slip),
			headers
		);
	}

	static getSlipByReference(referenceId: string): Promise<BetSlip> {
		let walletId =
			findValidSubmitWallet(App.state.rootStore.userAuthStore)?.id ||
			App.state.rootStore.userAuthStore.activeWalletId;

		const context = getApiRequestContext({
			context: RequestContextType.Wallet,
			walletId: walletId,
		});

		const headers = this.getRequestHeaders(context);
		let query = new URLSearchParams();
		query.set(
			"embed",
			"betStatus,betSlipStatus,betSlipType,betSlipOffers,betSlipSecureWinSetting"
		);

		query.set("translate", String(true));
		return httpClient.get(
			`${this.baseUrl(context)}/by-reference/${referenceId}?${query}`,
			headers,
			{
				responseType: "json",
			}
		);
	}

	static async prepare(
		slip: IBetSlipRequest,
		options?: ContextOptions
	): Promise<PrepareResponse> {
		const context = getApiRequestContext(options);

		const headers = this.getRequestHeaders(context);

		return await httpClient.post(
			`${this.baseUrl(context)}/prepare`,
			JSON.stringify(slip),
			headers
		);
	}

	static async fetchPreparedSlips(
		filter: Filter | null = null,
		page: number | null = 1,
		rpp: number = 10,
		sort: string = "dateCreated|desc",
		options?: ContextOptions
	): Promise<PageableCollection<PreparedBetSlip>> {
		const context = getApiRequestContext(options);

		const headers = this.getRequestHeaders(context);

		let query;
		if (filter) {
			//@ts-expect-error
			query = new URLSearchParams(filter);
		} else {
			query = new URLSearchParams();
		}
		query.set("page", String(page));
		query.set("rpp", String(rpp));
		query.set("sort", String(sort));

		return await httpClient.get(
			`${this.baseUrl(context)}/prepared?${query}`,
			headers
		);
	}

	static async deletePreparedSlip(
		preparedSlipId: string,
		options?: ContextOptions
	): Promise<void> {
		const context = getApiRequestContext(options);

		const headers = this.getRequestHeaders(context);

		return await httpClient.delete(
			`${this.baseUrl(context)}/prepared/${preparedSlipId}`,
			headers
		);
	}
}

export default BetSlipApiService;
